// Consent bridge. The OneTrust CMP is injected by GTM (same container as the main
// site) and exposes its state on `window.OnetrustActiveGroups` (a comma-wrapped list
// like ",C0001,C0002,") and fires `OneTrustGroupsUpdated` whenever consent changes.
// This module normalises that into the two "may we do X?" signals the site needs,
// independent of how OneTrust got onto the page.
//
// The two categories are NOT interchangeable, and the split mirrors the container.
// Web GTM tag 61 ("Consent Mode - OneTrust CMP") maps OneTrust categories onto
// Google Consent Mode like this:
//
//   analytics_storage                            <- C0002
//   ad_storage / ad_user_data / ad_personalization <- C0004
//
// So anything that is an advertising identifier (gclid, fbclid, ttclid and friends)
// belongs behind C0004, not C0002. A visitor may accept analytics and refuse
// advertising, and every Meta / TikTok / StackAdapt tag in the container already
// honours that distinction - the site has to as well.

declare global {
  interface Window {
    OnetrustActiveGroups?: string;
    dataLayer?: unknown[];
  }
}

// OneTrust category for analytics/performance cookies. Override per the container's
// configuration if the analytics tags live in a different group.
const ANALYTICS_GROUP =
  process.env.NEXT_PUBLIC_CONSENT_ANALYTICS_GROUP?.trim() || "C0002";

// OneTrust category for targeting/advertising cookies, i.e. the one the container
// maps onto ad_storage. Anything carrying an ad-platform click id needs this.
const ADVERTISING_GROUP =
  process.env.NEXT_PUBLIC_CONSENT_ADVERTISING_GROUP?.trim() || "C0004";

// Local/dev escape hatch: set NEXT_PUBLIC_CONSENT_FALLBACK=granted to treat consent
// as granted when GTM/OneTrust is not loaded (e.g. local experiment testing).
const FALLBACK_GRANTED =
  process.env.NEXT_PUBLIC_CONSENT_FALLBACK?.trim() === "granted";

// OnetrustActiveGroups lists only the groups the visitor has ACCEPTED, wrapped in
// commas (",C0001,C0002,"). Match on the wrapped id so a future "C0002A" style
// category cannot satisfy a check for "C0002".
function hasGroup(group: string): boolean {
  if (typeof window === "undefined") return false;
  const groups = window.OnetrustActiveGroups;
  if (typeof groups === "string" && groups.length > 0) {
    return `,${groups},`.replace(/,,+/g, ",").includes(`,${group},`);
  }
  // OneTrust not present yet (or no GTM): fall back only when explicitly allowed.
  return FALLBACK_GRANTED;
}

/** May we measure this visitor? (PostHog, first-party stats, UTM storage.) */
export function hasAnalyticsConsent(): boolean {
  return hasGroup(ANALYTICS_GROUP);
}

/**
 * May we store ad-platform identifiers? Gates the click ids in the first-touch
 * cookie. Deliberately separate from analytics consent - see the note above.
 */
export function hasAdvertisingConsent(): boolean {
  return hasGroup(ADVERTISING_GROUP);
}

// Subscribe to consent changes. Returns an unsubscribe function. OneTrust dispatches
// `OneTrustGroupsUpdated` on window whenever the banner is answered or the
// preference centre saved; `tvt:consent` is ours, for anything that needs to force
// a re-evaluation (nothing does today, but the CMP gives us no other hook).
export function onConsentChange(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => cb();
  window.addEventListener("OneTrustGroupsUpdated", handler);
  window.addEventListener("tvt:consent", handler);
  return () => {
    window.removeEventListener("OneTrustGroupsUpdated", handler);
    window.removeEventListener("tvt:consent", handler);
  };
}
